<template>
  <div>
    <EsAutocomplete
      v-bind="$attrs"
      @updateSearchInput="updateSearchInput"
      :items="allItems"
      :loading="loading"
      :itemValue="itemValue"
      :itemText="itemText"
      :itemDisplayText="itemDisplayText"
      no-filter
      @input="handleInput($event)"
      @disabledClick="$emit('disabledClick', $event)"
      :value="value"
      :sort="sort"
      menu-props="auto"
    >
      <template v-slot:append-item>
        <div v-intersect="showMore"></div>
      </template>
    </EsAutocomplete>
    <v-dialog
      v-if="isAddingNewItem"
      v-model="isAddingNewItem"
      width="500"
      persistent>
        <slot name="new-item"
          :onCancel="clearNewItem"
          :onSave="createAndSaveNewItem"></slot>
    </v-dialog>
</div>
</template>

<script>
import { EsAutocomplete } from '@86-repairs/design-system';
import { debounce, cloneDeep } from 'lodash';

export default {
  name: 'EsAutocompletePaginated',
  props: {
    searchFilter: {
      type: Object,
      default: () => ({}),
    },
    getItems: {
      type: Function,
      required: true,
    },
    getItemById: {
      type: Function,
      required: true,
    },
    limit: {
      type: Number,
      default: 100,
    },
    searchParam: {
      type: String,
      default: 'filterText',
    },
    value: {
      type: [String, Array, Object, Number],
      required: false,
    },
    itemValue: {
      type: String,
      required: true,
    },
    itemText: {
      type: String,
      required: true,
    },
    itemDisplayText: {
      type: String,
      required: false,
    },
    canAddNew: {
      type: Boolean,
      default: false,
    },
    createNewItem: {
      type: Function,
    },
    sort: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EsAutocomplete,
  },
  data: () => ({
    items: [],
    page: 0,
    totalItems: 0,
    lastPage: 1,
    loading: false,
    filter: {},
    prevSearchTerm: null,
    isAddingNewItem: false,
  }),
  computed: {
    allItems() {
      const addItem = {};
      addItem[this.itemText] = '-- Add New --';
      addItem[this.itemValue] = -1;
      return !this.canAddNew ? this.items : [addItem, ...this.items];
    },
  },
  watch: {
    searchFilter: {
      handler() {
        this.filter = cloneDeep(this.searchFilter);
      },
      deep: true,
      immediate: true,
    },
    value: {
      handler(val) {
        if (Array.isArray(val)) {
          val.forEach((item) => {
            this.getItemIfNotExists(item);
          });
        } else if (val) {
          this.getItemIfNotExists(val);
        }
      },
      deep: true,
      immediate: true,
    },
    loading(value) {
      this.$emit('isLoading', value);
    },
  },
  methods: {
    handleInput(value) {
      if (value) {
        if (value?.id === -1 || value === -1) {
          this.isAddingNewItem = true;
        } else {
          this.$emit('input', value);
        }
      } else {
        this.$emit('clear');
      }
    },
    updateSearchInput: debounce(function updateSearchInput(searchInput) {
      if (searchInput === this.prevSearchTerm) { return; }
      if (searchInput === null) { return; }
      this.reset();
      if (searchInput === '') {
        this.prevSearchTerm = null;
        this.$set(this.filter, this.searchParam, null);
      } else {
        this.prevSearchTerm = searchInput;
        this.$set(this.filter, this.searchParam, searchInput);
      }
      this.get(true);
    }, 500),
    showMore(entries) {
      if (entries && entries[0].isIntersecting) {
        this.get();
      }
    },
    get(reset = false) {
      if (this.loading || this.lastPage <= this.page) {
        return;
      }
      this.loading = true;
      const query = { ...this.filter, offset: this.page * this.limit, limit: this.limit };
      this.getItems(query).then((response) => {
        if (reset) { this.page = 0; }
        this.items = this.page === 0 ? response.data : [...this.items, ...response.data];
        this.totalItems = response.count;
        this.lastPage = Math.ceil(response.count / this.limit);
        this.page++;
      }).finally(() => {
        this.loading = false;
      });
    },
    reset() {
      this.page = 0;
      this.items = [];
      this.totalItems = 0;
      this.lastPage = 1;
      this.loading = false;
      this.filter = this.searchFilter;
    },
    getItemIfNotExists(id) {
      if (id) {
        this.getItemById(id)?.then((response) => {
          this.items = [...this.items, response];
        });
      }
    },
    clearNewItem() {
      this.isAddingNewItem = false;
      this.reset();
      this.get();
    },
    async createAndSaveNewItem(item) {
      const newItem = await this.createNewItem(item);
      this.items = [...this.items, newItem];
      this.handleInput(newItem);
      this.isAddingNewItem = false;
    },
  },
};
</script>
